<template>
    <div class="main-panel">
        <div class="page-container">

            <!-- Page header -->
            <div class="page-header">
                <h1 class="page-title">Bulk Credit Upload</h1>
                <p class="page-subtitle">Upload a CSV of User Ids to add (or remove) credit for multiple users at once.</p>
            </div>

            <!-- Step 1: Upload CSV -->
            <div class="content-card">
                <div class="content-card-header">
                    <span class="card-title">Upload CSV</span>
                </div>
                <div class="content-card-body">

                    <div class="input-row">
                        <div class="field-group">
                            <label class="field-label" for="csvFile">CSV File (UserId column)</label>
                            <input id="csvFile"
                                   class="field-input"
                                   type="file"
                                   accept=".csv"
                                   ref="fileInput"
                                   @change="onFileSelected" />
                        </div>
                    </div>

                    <p v-if="userIds.length" class="empty-hint">
                        {{ userIds.length }} UserId{{ userIds.length === 1 ? '' : 's' }} parsed from the file.
                    </p>

                    <div class="input-row">
                        <div class="field-group">
                            <label class="field-label" for="creditAmount">Credit Amount (£)</label>
                            <input id="creditAmount"
                                   class="field-input"
                                   type="number"
                                   step="0.01"
                                   v-model.number="creditAmount"
                                   placeholder="e.g. 10 or -10" />
                        </div>
                        <div class="field-group field-group--wide">
                            <label class="field-label" for="description">Transaction Description</label>
                            <input id="description"
                                   class="field-input"
                                   type="text"
                                   v-model="description"
                                   placeholder="e.g. Goodwill gesture for service disruption" />
                        </div>
                    </div>

                    <button class="btn-add"
                            @click="submitBulkCredit"
                            :disabled="!canSubmit || isSubmitting">
                        <span v-if="!isSubmitting">Add Credit</span>
                        <span v-else class="btn-spinner"></span>
                    </button>

                    <!-- Results panel -->
                    <div v-if="results.length" class="results-card">
                        <div class="results-header">
                            <span class="results-title">Results</span>
                            <span class="results-count">{{ successCount }} of {{ results.length }} succeeded</span>
                        </div>
                        <div class="ae-results-body">
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th class="col-num">#</th>
                                        <th>User ID</th>
                                        <th>Status</th>
                                        <th>Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(result, index) in results" :key="index">
                                        <td class="col-num muted">{{ index + 1 }}</td>
                                        <td><span class="user-id">{{ result.userId }}</span></td>
                                        <td>
                                            <span :class="result.isSuccess ? 'status-success' : 'status-error'">
                                                {{ result.isSuccess ? 'Success' : 'Failed' }}
                                            </span>
                                        </td>
                                        <td>{{ result.message }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>

        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, ref, computed } from 'vue';
    import userService from '../services/user-service';
    import BulkCreditResult from '../responses/BulkCreditResult';
    import { showAlert } from '../utils/global';

    export default defineComponent({
        name: 'BulkCreditUploadView',
        setup() {
            const fileInput = ref<HTMLInputElement | null>(null);
            const userIds = ref<number[]>([]);
            const creditAmount = ref<number | null>(null);
            const description = ref<string>('');
            const isSubmitting = ref(false);
            const results = ref<BulkCreditResult[]>([]);

            const canSubmit = computed(() =>
                userIds.value.length > 0 &&
                !!creditAmount.value &&
                description.value.trim().length > 0
            );

            const successCount = computed(() => results.value.filter(r => r.isSuccess).length);

            function onFileSelected(event: Event) {
                const input = event.target as HTMLInputElement;
                const file = input.files?.[0];

                userIds.value = [];
                results.value = [];

                if (!file) return;

                const reader = new FileReader();
                reader.onload = () => {
                    const text = reader.result as string;
                    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);

                    const parsedIds: number[] = [];

                    for (const line of lines) {
                        const firstColumn = line.split(',')[0].trim();
                        const value = Number(firstColumn);

                        if (!isNaN(value) && Number.isInteger(value) && value > 0) {
                            parsedIds.push(value);
                        }
                    }

                    userIds.value = Array.from(new Set(parsedIds));
                };
                reader.readAsText(file);
            }

            async function submitBulkCredit() {
                if (!canSubmit.value) return;

                isSubmitting.value = true;
                results.value = [];

                try {
                    const response = await userService.bulkAddUserCreditBalance({
                        userIds: userIds.value,
                        creditAmount: creditAmount.value!,
                        description: description.value.trim(),
                    });

                    results.value = response.results;

                    if (!response.isSuccess) {
                        showAlert('Bulk Credit Upload', response.message, 'warning');
                    }
                } catch (error: any) {
                    const message =
                        error?.response?.data?.errors?.[0] ??
                        error?.response?.data?.message ??
                        error?.message ??
                        'An unexpected error occurred.';
                    showAlert('Failed to Add Bulk Credit', message, 'error');
                } finally {
                    isSubmitting.value = false;
                }
            }

            return {
                fileInput,
                userIds,
                creditAmount,
                description,
                isSubmitting,
                results,
                canSubmit,
                successCount,
                onFileSelected,
                submitBulkCredit,
            };
        },
    });
</script>

<style scoped>

    .page-container {
        padding: 2rem 2.5rem;
        max-width: 860px;
    }

    .page-header {
        margin-bottom: 2rem;
        padding-bottom: 1.25rem;
        border-bottom: 2px solid var(--clr-primary);
    }

    .page-title {
        font-size: 1.6rem;
        font-weight: 700;
        color: var(--clr-primary);
        margin-bottom: 0.3rem;
    }

    .page-subtitle {
        font-size: 0.9rem;
        color: #666;
    }

    .content-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        margin-bottom: 1.25rem;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .content-card-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1.25rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e0e0e0;
    }

    .card-title {
        font-size: 0.9rem;
        font-weight: 600;
        color: #333;
    }

    .content-card-body {
        padding: 1rem 1.25rem;
    }

    .input-row {
        display: flex;
        gap: 1rem;
        align-items: flex-end;
        flex-wrap: wrap;
        margin-bottom: 1rem;
    }

    .field-group {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
    }

    .field-group--wide {
        flex: 1;
        min-width: 260px;
    }

    .field-label {
        font-size: 0.8rem;
        font-weight: 600;
        color: #555;
    }

    .field-input {
        padding: 0.5rem 0.65rem;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 0.9rem;
        min-width: 220px;
    }

    .btn-add {
        background: var(--clr-primary);
        color: #fff;
        border: none;
        border-radius: 4px;
        padding: 0.55rem 1.25rem;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.15s;
    }

    .btn-add:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .btn-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255, 255, 255, 0.4);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    .empty-hint {
        font-size: 0.85rem;
        color: #777;
        margin-bottom: 1rem;
    }

    .results-card {
        margin-top: 1.5rem;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        overflow: hidden;
    }

    .results-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.65rem 1rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e0e0e0;
    }

    .results-title {
        font-size: 0.85rem;
        font-weight: 700;
        color: #333;
    }

    .results-count {
        font-size: 0.8rem;
        color: #666;
        margin-left: auto;
    }

    .ae-results-body {
        max-height: 420px;
        overflow-y: auto;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
    }

    .data-table th,
    .data-table td {
        padding: 0.5rem 0.75rem;
        font-size: 0.85rem;
        border-bottom: 1px solid #eee;
        text-align: left;
    }

    .col-num {
        width: 40px;
    }

    .muted {
        color: #999;
    }

    .user-id {
        font-weight: 600;
        color: #333;
    }

    .status-success {
        color: #1a7f37;
        font-weight: 600;
    }

    .status-error {
        color: #c0392b;
        font-weight: 600;
    }

</style>
